<script setup lang="ts">
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';

export interface Status {
  color?: 'gray' | 'grey' | 'green' | 'red' | 'orange' | 'violet';
  icon?: string;
  hint?: string;
  width?: number;
  isBlink?: boolean;
  tooltip?: string | boolean;
  action?: () => void;
}

export interface Props {
  statuses?: Status[];
}
const props = withDefaults(defineProps<Props>(), {
  statuses: () => [],
});

const defStatusColor: Status['color'] = 'gray';
const defStatusWidth = 80;

function handleCircleClick(event: Event, status: Status): void {
  if (!status.action) return;
  status.action();
}

const withActionTooltipText = 'Click for action';
const withoutActionTooltipText = 'There is no action';
</script>

<template>
  <div
    :class="$style.smallTrafficLightDiagram"
    data-testid="smallTrafficLightDiagram"
  >
    <div :class="$style.statuses">
      <div
        v-if="!props.statuses.length"
        :class="$style.status"
        :style="`min-width: ${defStatusWidth}px`"
      >
        <div :class="[$style.circle, $style['circle_' + defStatusColor]]"></div>
      </div>
      <div
        v-for="(status, statusIndex) in props.statuses"
        :key="statusIndex"
        :class="$style.status"
        :style="`min-width: ${status.width ?? defStatusWidth}px`"
      >
        <div v-if="props.statuses.length > 1" :class="$style.road"></div>
        <button
          type="button"
          :class="[
            $style.circle,
            $style['circle_' + (status.color ?? defStatusColor)],
            { [$style.circle_blink]: status.isBlink },
          ]"
          :disabled="!status.action"
          @click="handleCircleClick($event, status)"
        >
          <template v-if="status.tooltip !== false">
            <KParentTooltip location="top" :offset="3">
              <!-- prettier-ignore -->
              {{
                status.tooltip ||
                (status.action
                  ? withActionTooltipText
                  : withoutActionTooltipText)
              }}
              <!-- eslint-enable prettier/prettier -->
            </KParentTooltip>
          </template>
          <v-icon v-if="status.icon" :icon="status.icon"></v-icon>
        </button>
        <div :class="$style.hint">{{ status.hint }}</div>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';

$circle-size: 25px;
$road-height: 2px;

.smallTrafficLightDiagram {
  height: 52px;
  line-height: 1.4;
}
  .statuses {
    display: inline-flex;
  }
    .status {
      position: relative;
      z-index: 0;
      text-align: center;
    }
      .road {
        height: $road-height;
        background-color: colors.$color-gray-300;
        width: 100%;
        position: absolute;
        z-index: -1;
        margin-top: math.div($circle-size, 2) - math.div($road-height, 2);
      }
      .status:first-child .road {
        width: 50%;
        right: 0;
      }
      .status:last-child .road {
        width: 50%;
      }
      .circle {
        border-radius: 50%;
        background-color: colors.$color-text-disabled;
        border: $road-height solid colors.$color-gray-200;
        width: $circle-size;
        height: $circle-size;
        display: block;
        margin: 0 auto;
        color: colors.$color-white;
        font-size: 9px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;

        &:not(:disabled) {
          &:hover {
            border: 2px solid colors.$color-blue-300;
          }
          &:active {
            border: 2px solid colors.$color-gray-200;
          }
        }
      }
      .circle_green {
        background-color: colors.$color-status-success-icon;
      }
      .circle_red {
        background-color: colors.$color-red-500;
      }
      .circle_orange {
        background-color: colors.$color-orange-400;
      }
      .circle_violet {
        background-color: colors.$color-blue-400;
      }
      .circle_blink {
        animation: blinker 1s step-start infinite;
      }
      @keyframes blinker {
        50% {
          background-color: colors.$color-text-disabled;
        }
      }
      .hint {
        font-size: 12px;
        font-weight: 500;
        margin-top: -2px;
      }
</style>
